import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Alert,
  Button,
  EmptyState,
  Filters,
  LoadingState,
  Pagination,
  Search,
  SectionHeader,
  Select,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createBookingsApi } from "../api";
import { bookingStatusTone, formatLabel, holdStatusTone } from "../labels";
import { formatMoney, formatWindow, PAYMENT_LABEL, paymentTone } from "../format";
import type {
  BookingDto,
  BookingSummary,
  BookingStatus,
  HoldDto,
  HoldStatus,
  ScheduleConflictReason,
} from "../types";
import { CreateBookingDialog } from "../components/BookingDialogs";

const BOOKING_STATUSES: BookingStatus[] = [
  "initiated",
  "pending_confirmation",
  "pending_payment",
  "confirmed",
  "in_progress",
  "completed",
  "cancelled",
  "failed",
  "disputed",
  "refunded_partial",
  "refunded_full",
];

const HOLD_STATUSES: HoldStatus[] = [
  "active",
  "expiring",
  "converted",
  "released",
  "expired",
  "cancelled",
  "superseded",
];

export function BookingsDirectoryPage() {
  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Bookings" },
  ]);

  const client = useApiClient();
  const api = useMemo(() => createBookingsApi(client), [client]);
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const q = searchParams.get("q") ?? "";
  const status = (searchParams.get("status") ?? "") as BookingStatus | "";
  const businessId = searchParams.get("business_id") ?? "";
  const resourceId = searchParams.get("bookable_resource_id") ?? "";
  const sort = searchParams.get("sort") ?? "-starts_at";
  const cursor = searchParams.get("cursor") ?? undefined;

  const [rows, setRows] = useState<BookingDto[]>([]);
  // Names, money and attendance per row, loaded with the page. A failure keeps
  // the table usable and says what is missing.
  const [summaries, setSummaries] = useState<Record<string, BookingSummary>>({});
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchDraft, setSearchDraft] = useState(q);
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [conflictResult, setConflictResult] = useState<{
    clear: boolean;
    conflicts: ScheduleConflictReason[];
  } | null>(null);

  const page = cursorStack.length + 1;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await api.list({
        ...(cursor ? { cursor } : {}),
        limit: 25,
        sort,
        ...(q ? { q } : {}),
        ...(status ? { status } : {}),
        ...(businessId ? { business_id: businessId } : {}),
        ...(resourceId ? { bookable_resource_id: resourceId } : {}),
      });
      setRows(list.bookings);
      setNextCursor(list.pagination.next_cursor);
      setSummaryError(null);
      if (list.bookings.length > 0) {
        try {
          setSummaries((await api.summaries(list.bookings.map((b) => b.id))).summaries);
        } catch (err) {
          setSummaries({});
          setSummaryError(apiErrorMessage(err));
        }
      } else {
        setSummaries({});
      }
    } catch (err) {
      setError(apiErrorMessage(err));
      setRows([]);
      setNextCursor(null);
    } finally {
      setLoading(false);
    }
  }, [api, cursor, sort, q, status, businessId, resourceId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setSearchDraft(q);
  }, [q]);

  function updateParams(patch: Record<string, string | undefined>) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined || value === "") next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next);
  }

  function applySearch() {
    setCursorStack([]);
    updateParams({ q: searchDraft.trim() || undefined, cursor: undefined });
  }

  return (
    <Stack gap={24}>
      <SectionHeader
        title="Bookings"
        description="Operate reservations. A Hold is a separate inventory lock, never a Booking status. Refunds here change Booking status only — money stays in Finance."
        actions={
          <Flex gap={8}>
            <Button
              variant="secondary"
              onClick={() => void navigate("/app/bookings/holds")}
            >
              Holds
            </Button>
            <Button
              variant="secondary"
              onClick={() => void navigate("/app/bookings/events")}
            >
              Events
            </Button>
            <Button variant="secondary" onClick={() => setCreateOpen(true)}>
              Create Booking
            </Button>
            <Button variant="secondary" onClick={() => void load()}>
              Refresh
            </Button>
          </Flex>
        }
      />

      <Filters>
        <div style={{ flex: "1 1 240px", minWidth: 200 }}>
          <Search
            value={searchDraft}
            onValueChange={setSearchDraft}
            placeholder="Code (BK-…), player or venue name"
            onKeyDown={(event) => {
              if (event.key === "Enter") applySearch();
            }}
          />
        </div>
        <Select
          label="Status"
          value={status}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({
              status: event.target.value || undefined,
              cursor: undefined,
            });
          }}
          options={[
            { value: "", label: "All statuses" },
            ...BOOKING_STATUSES.map((item) => ({
              value: item,
              label: formatLabel(item),
            })),
          ]}
        />
        <Select
          label="Sort"
          value={sort}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({ sort: event.target.value, cursor: undefined });
          }}
          options={[
            { value: "-starts_at", label: "Starts (newest)" },
            { value: "starts_at", label: "Starts (oldest)" },
            { value: "status", label: "Status" },

          ]}
        />
        <Button variant="primary" onClick={applySearch}>
          Apply search
        </Button>
      </Filters>

      {error ? (
        <Alert tone="danger" title="Unable to load Bookings">
          {error}
        </Alert>
      ) : null}

      {summaryError ? (
        <Alert tone="warning" title="Names, payments and check-ins could not be loaded">
          {summaryError}
        </Alert>
      ) : null}

      {loading ? <LoadingState message="Loading Bookings…" /> : null}

      {!loading && !error && rows.length === 0 ? (
        <EmptyState
          reason={q || status || businessId ? "filters" : "none"}
          title="No Bookings found"
        />
      ) : null}

      {!loading && rows.length > 0 ? (
        <Stack gap={12}>
          <ActionBar
            left={
              <Text role="small" color="secondary" as="span">
                Showing {rows.length} booking{rows.length === 1 ? "" : "s"}
              </Text>
            }
            right={
              <Pagination
                page={page}
                pageCount={nextCursor ? page + 1 : page}
                onPageChange={(nextPage) => {
                  if (nextPage > page && nextCursor) {
                    setCursorStack((stack) => [...stack, cursor ?? ""]);
                    updateParams({ cursor: nextCursor });
                  }
                  if (nextPage < page && cursorStack.length > 0) {
                    const stack = [...cursorStack];
                    const previous = stack.pop();
                    setCursorStack(stack);
                    updateParams({
                      cursor:
                        previous && previous.length > 0 ? previous : undefined,
                    });
                  }
                }}
              />
            }
          />
          <Table>
            <Thead>
              <Tr>
                <Th>Code</Th>
                <Th>Player</Th>
                <Th>Venue</Th>
                <Th>When</Th>
                <Th>Status</Th>
                <Th>Payment</Th>
                <Th>Attendance</Th>
                <Th>Amount</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row) => {
                const s = summaries[row.id];
                return (
                <Tr key={row.id}>
                  <Td>
                    <Text role="bodyStrong" as="div">
                      {row.display_code ?? "—"}
                    </Text>
                  </Td>
                  <Td>{s?.customer_name ?? "—"}</Td>
                  <Td>
                    <Text role="body" as="div">{s?.business_name ?? "—"}</Text>
                    {s?.resource_name ? (
                      <Text role="caption" color="muted" as="div">{s.resource_name}</Text>
                    ) : null}
                  </Td>
                  <Td>{formatWindow(row.starts_at, row.ends_at, row.booking_timezone)}</Td>
                  <Td>
                    <StatusChip tone={bookingStatusTone(row.status)}>
                      {formatLabel(row.status)}
                    </StatusChip>
                  </Td>
                  <Td>
                    {s ? (
                      <StatusChip tone={paymentTone(s.payment.status)}>
                        {PAYMENT_LABEL[s.payment.status]}
                      </StatusChip>
                    ) : "—"}
                  </Td>
                  <Td>
                    {row.checked_in_at ? (
                      <StatusChip tone="success">Checked in</StatusChip>
                    ) : row.no_show_at ? (
                      <StatusChip tone="danger">No-show</StatusChip>
                    ) : (
                      <Text role="caption" color="muted" as="span">—</Text>
                    )}
                  </Td>
                  <Td>{formatMoney(s?.amount ?? null, row.currency)}</Td>
                  <Td>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => void navigate(`/app/bookings/${row.id}`)}
                    >
                      Open
                    </Button>
                  </Td>
                </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Stack>
      ) : null}

      <CreateBookingDialog
        open={createOpen}
        busy={createBusy}
        error={createError}
        conflictResult={conflictResult}
        {...(businessId ? { defaultBusinessId: businessId } : {})}
        {...(resourceId ? { defaultResourceId: resourceId } : {})}
        onClose={() => {
          setCreateOpen(false);
          setCreateError(null);
          setConflictResult(null);
        }}
        onCheckConflicts={async (input) => {
          setCreateBusy(true);
          setCreateError(null);
          try {
            const result = await api.checkConflicts(input);
            setConflictResult({
              clear: result.clear,
              conflicts: result.conflicts,
            });
          } catch (err) {
            setCreateError(apiErrorMessage(err));
          } finally {
            setCreateBusy(false);
          }
        }}
        onSubmit={async (input) => {
          setCreateBusy(true);
          setCreateError(null);
          try {
            const result = await api.create(input);
            toast.push({
              title: "Booking created",
              ...(result.booking.display_code ? { description: result.booking.display_code } : {}),
              tone: "success",
            });
            setCreateOpen(false);
            void navigate(`/app/bookings/${result.booking.id}`);
          } catch (err) {
            setCreateError(apiErrorMessage(err));
          } finally {
            setCreateBusy(false);
          }
        }}
      />
    </Stack>
  );
}

export function HoldsDirectoryPage() {
  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Bookings", to: "/app/bookings" },
    { label: "Holds" },
  ]);

  const client = useApiClient();
  const api = useMemo(() => createBookingsApi(client), [client]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const q = searchParams.get("q") ?? "";
  const status = (searchParams.get("status") ?? "") as HoldStatus | "";
  const businessId = searchParams.get("business_id") ?? "";
  const resourceId = searchParams.get("bookable_resource_id") ?? "";
  const sort = searchParams.get("sort") ?? "expires_at";
  const cursor = searchParams.get("cursor") ?? undefined;

  const [rows, setRows] = useState<HoldDto[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchDraft, setSearchDraft] = useState(q);
  const page = cursorStack.length + 1;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await api.listHolds({
        ...(cursor ? { cursor } : {}),
        limit: 25,
        sort,
        ...(q ? { q } : {}),
        ...(status ? { status } : {}),
        ...(businessId ? { business_id: businessId } : {}),
        ...(resourceId ? { bookable_resource_id: resourceId } : {}),
      });
      setRows(list.holds);
      setNextCursor(list.pagination.next_cursor);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRows([]);
      setNextCursor(null);
    } finally {
      setLoading(false);
    }
  }, [api, cursor, sort, q, status, businessId, resourceId]);

  useEffect(() => {
    void load();
  }, [load]);

  function updateParams(patch: Record<string, string | undefined>) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined || value === "") next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next);
  }

  return (
    <Stack gap={24}>
      <SectionHeader
        title="Booking Allocation Holds"
        description="A Hold reserves supply while payment is pending. It is not a Booking status."
        actions={
          <Flex gap={8}>
            <Button
              variant="secondary"
              onClick={() => void navigate("/app/bookings")}
            >
              Bookings
            </Button>
            <Button variant="secondary" onClick={() => void load()}>
              Refresh
            </Button>
          </Flex>
        }
      />

      <Filters>
        <div style={{ flex: "1 1 240px", minWidth: 200 }}>
          <Search
            value={searchDraft}
            onValueChange={setSearchDraft}
            placeholder="Search public ID"
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                setCursorStack([]);
                updateParams({
                  q: searchDraft.trim() || undefined,
                  cursor: undefined,
                });
              }
            }}
          />
        </div>
        <Select
          label="Status"
          value={status}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({
              status: event.target.value || undefined,
              cursor: undefined,
            });
          }}
          options={[
            { value: "", label: "All statuses" },
            ...HOLD_STATUSES.map((item) => ({
              value: item,
              label: formatLabel(item),
            })),
          ]}
        />
        <Select
          label="Sort"
          value={sort}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({ sort: event.target.value, cursor: undefined });
          }}
          options={[
            { value: "expires_at", label: "Expires (soonest)" },
            { value: "-expires_at", label: "Expires (latest)" },
            { value: "starts_at", label: "Starts" },
            { value: "status", label: "Status" },
          ]}
        />
        <Button
          variant="primary"
          onClick={() => {
            setCursorStack([]);
            updateParams({
              q: searchDraft.trim() || undefined,
              cursor: undefined,
            });
          }}
        >
          Apply search
        </Button>
      </Filters>

      {error ? (
        <Alert tone="danger" title="Unable to load Holds">
          {error}
        </Alert>
      ) : null}
      {loading ? <LoadingState message="Loading Holds…" /> : null}
      {!loading && !error && rows.length === 0 ? (
        <EmptyState title="No Holds found" reason="none" />
      ) : null}

      {!loading && rows.length > 0 ? (
        <Stack gap={12}>
          <ActionBar
            left={
              <Text role="small" color="secondary" as="span">
                Showing {rows.length} hold{rows.length === 1 ? "" : "s"}
              </Text>
            }
            right={
              <Pagination
                page={page}
                pageCount={nextCursor ? page + 1 : page}
                onPageChange={(nextPage) => {
                  if (nextPage > page && nextCursor) {
                    setCursorStack((stack) => [...stack, cursor ?? ""]);
                    updateParams({ cursor: nextCursor });
                  }
                  if (nextPage < page && cursorStack.length > 0) {
                    const stack = [...cursorStack];
                    const previous = stack.pop();
                    setCursorStack(stack);
                    updateParams({
                      cursor:
                        previous && previous.length > 0 ? previous : undefined,
                    });
                  }
                }}
              />
            }
          />
          <Table>
            <Thead>
              <Tr>
                <Th>Public ID</Th>
                <Th>Status</Th>
                <Th>Window</Th>
                <Th>Expires</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row) => (
                <Tr key={row.id}>
                  <Td>{row.public_id}</Td>
                  <Td>
                    <StatusChip tone={holdStatusTone(row.status)}>
                      {formatLabel(row.status)}
                    </StatusChip>
                  </Td>
                  <Td>
                    {row.starts_at} → {row.ends_at}
                  </Td>
                  <Td>{row.expires_at}</Td>
                  <Td>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() =>
                        void navigate(`/app/bookings/holds/${row.id}`)
                      }
                    >
                      Open
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Stack>
      ) : null}
    </Stack>
  );
}
